/**
 * TextField — Apple-style form input with floating label.
 *
 * Label sits inside the field as placeholder, then floats up
 * small when focused or filled. Reduces visual noise.
 *
 * Handles text, email, tel, password, and multiline (textarea).
 * Single component — no separate TextArea needed.
 *
 * Usage:
 *   <TextField label="Email" value={email} onChange={e => setEmail(e.target.value)} type="email" />
 *   <TextField label="Notes" value={notes} onChange={e => setNotes(e.target.value)} multiline maxLength={100} />
 */

import { useState, useId, useRef, useEffect } from 'react';
import { ds } from '../tokens';
import { CharacterCounter } from './CharacterCounter';

export interface TextFieldProps {
  label?: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  type?: 'text' | 'email' | 'tel' | 'password';
  /** @deprecated Use formatHint instead for trailing format hints */
  placeholder?: string;
  /** Trailing format hint displayed on right side (e.g., "e.g. 45000", "MM/DD/YYYY") */
  formatHint?: string;
  multiline?: boolean;
  rows?: number;
  maxLength?: number;
  error?: string;
  helperText?: string;
  disabled?: boolean;
  required?: boolean;
  trailingIcon?: React.ReactNode;
  onTrailingIconClick?: () => void;
  trailingIconAriaLabel?: string;
  className?: string;
  id?: string;
  /** Auto-focus the input when mounted (uses short delay for sheet/modal transitions) */
  autoFocus?: boolean;
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
}

export function TextField({
  label,
  value,
  onChange,
  onFocus: onFocusProp,
  onBlur,
  onKeyDown,
  type = 'text',
  placeholder,
  formatHint,
  multiline = false,
  rows = 3,
  maxLength,
  error,
  helperText,
  disabled = false,
  required: _required = false,
  trailingIcon,
  onTrailingIconClick,
  trailingIconAriaLabel,
  className = '',
  id: externalId,
  autoFocus = false,
  inputMode,
}: TextFieldProps) {
  const [isFocused, setIsFocused] = useState(false);
  const autoId = useId();
  const inputId = externalId || autoId;
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      const timer = setTimeout(() => inputRef.current?.focus(), 100);
      return () => clearTimeout(timer);
    }
  }, [autoFocus]);

  const isFloated = isFocused || !!value;

  const borderColor = error
    ? ds.color.state.errorBorder
    : isFocused
      ? ds.color.state.focusBorder
      : ds.color.separator.field;

  const ringClass = isFocused ? ds.color.state.focusRing : '';

  const handleFocus = () => { setIsFocused(true); onFocusProp?.(); };
  const handleBlur = () => {
    setIsFocused(false);
    onBlur?.();
  };

  const inputClasses = [
    'w-full bg-transparent outline-none px-3',
    label ? (multiline ? 'pt-6 pb-2' : 'pt-6 pb-1.5') : 'py-2.5',
    ds.type.body,
    ds.type.weight.medium,
    ds.color.label.primary,
    ds.color.label.placeholder,
    disabled ? 'opacity-50 cursor-not-allowed' : '',
    trailingIcon ? 'pr-10' : (formatHint && !value) ? 'pr-24' : '',
    multiline ? 'resize-none' : '',
    !multiline && !isFocused ? 'text-ellipsis overflow-hidden' : '',
  ].filter(Boolean).join(' ');

  const labelText = label || '';

  return (
    <div className={className}>
      {/* Input container with floating label */}
      <div
        className={[
          'relative overflow-hidden',
          ds.color.fill.primary,
          ds.radius.md,
          'border',
          borderColor,
          ringClass,
          ds.transition.fast,
        ].filter(Boolean).join(' ')}
      >
        {/* Floating label — multiline gets bg strip so text scrolls beneath it */}
        {label && (
          <label
            htmlFor={inputId}
            className={[
              'absolute pointer-events-none origin-top-left',
              isFloated && multiline ? 'left-0 right-0 px-3 pb-1 z-[1]' : 'left-3',
              isFloated && multiline ? ds.color.fill.primary : '',
              ds.transition.label,
              isFloated
                ? [
                    multiline ? 'top-0 pt-1.5' : 'top-1.5',
                    multiline ? 'text-xs' : 'scale-[0.75]',
                    ds.color.label.floated,
                  ].join(' ')
                : [
                    multiline ? 'top-3' : 'top-1/2 -translate-y-1/2',
                    ds.type.body,
                    ds.color.label.tertiary,
                  ].join(' '),
            ].join(' ')}
          >
            {labelText}
          </label>
        )}

        {multiline ? (
          <textarea
            ref={inputRef as React.RefObject<HTMLTextAreaElement>}
            id={inputId}
            value={value}
            onChange={onChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={onKeyDown}
            placeholder={isFocused ? placeholder : undefined}
            maxLength={maxLength}
            disabled={disabled}
            rows={rows}
            className={`${inputClasses} ${ds.layout.scrollbar}`}
            style={{ scrollPaddingTop: label ? '1.5rem' : undefined }}
          />
        ) : (
          <input
            ref={inputRef as React.RefObject<HTMLInputElement>}
            id={inputId}
            type={type}
            value={value}
            onChange={onChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={onKeyDown}
            placeholder={isFocused ? placeholder : undefined}
            maxLength={maxLength}
            disabled={disabled}
            className={inputClasses}
            inputMode={inputMode}
          />
        )}

        {/* Trailing icon (e.g., password visibility toggle) */}
        {trailingIcon && (
          onTrailingIconClick ? (
            <button
              type="button"
              onClick={onTrailingIconClick}
              className={[
                'absolute right-3 top-1/2 -translate-y-1/2',
                ds.color.label.secondary,
                ds.transition.fast,
              ].join(' ')}
              tabIndex={-1}
              aria-label={trailingIconAriaLabel}
            >
              {trailingIcon}
            </button>
          ) : (
            <div
              className={[
                'absolute right-3 top-1/2 -translate-y-1/2',
                ds.color.label.secondary,
                ds.transition.fast,
              ].join(' ')}
            >
              {trailingIcon}
            </div>
          )
        )}

        {/* Format hint — only when empty (Apple hides hints once filled) */}
        {formatHint && !trailingIcon && !value && (
          <span className={ds.field.formatHint}>
            {formatHint}
          </span>
        )}
      </div>

      {/* Error message */}
      {error && (
        <p className={[ds.type.caption1, ds.color.label.destructive, 'mt-1 px-1'].join(' ')}>
          {error}
        </p>
      )}

      {/* Helper text (hidden when error is shown) */}
      {helperText && !error && (
        <p className={[ds.type.caption1, ds.color.label.secondary, 'mt-1 px-1'].join(' ')}>
          {helperText}
        </p>
      )}

      {/* Auto character counter (multiline only) */}
      {multiline && maxLength && <CharacterCounter value={value} max={maxLength} className="px-1" />}
    </div>
  );
}
